import { useState, useCallback } from 'react';
import { useOrganization } from '@/contexts/OrganizationContext';
import { organizationService } from '@/firebase/organizationService';
import { getTaskCategoriesForOrganization } from '@/firebase/taskCategoryService';
import { mergeTaskCategories } from '@/constants/taskCategories';

/**
 * Returns task categories for the current organization (industry defaults + custom)
 * and a function to add a new custom category.
 * Optimistically appends new categories to the list so the dropdown updates without refetching org.
 */
export function useTaskCategories() {
  const { currentOrganization, refreshOrganization } = useOrganization();
  const [locallyAdded, setLocallyAdded] = useState<string[]>([]);

  const baseCategories = getTaskCategoriesForOrganization(currentOrganization);
  const categories = mergeTaskCategories(baseCategories, locallyAdded);

  const addCustomCategory = useCallback(
    async (label: string): Promise<{ success: boolean; error?: string }> => {
      const orgId = currentOrganization?.id;
      if (!orgId) {
        return { success: false, error: 'No organization selected' };
      }
      const trimmed = (typeof label === 'string' ? label : '').trim();
      if (!trimmed) {
        return { success: false, error: 'Category name is required' };
      }
      const result = await organizationService.addCustomTaskCategory(orgId, trimmed);
      if (result.success) {
        setLocallyAdded((prev) => (prev.some((c) => c.trim().toLowerCase() === trimmed.toLowerCase()) ? prev : [...prev, trimmed]));
        await refreshOrganization();
      }
      return result;
    },
    [currentOrganization?.id, refreshOrganization]
  );

  return { categories, addCustomCategory };
}
